import { useState, useCallback } from "react";
import { useFormContext } from "@/context/FormContext";
import { useGlobalStateContext } from "@/context/GlobalContext";
import NutrientService from "@/services/nutritionService";
import { transformGetNutritionResponse } from "@/utils/nutritionApiTransformer";
import { NutritionFormInitial } from "@/static/initial/Nutrition";
import { GetNutritionSectionsResponse } from "@/types/Nutrition.types";
import useActiveProjectValidation from "@/components/ActiveProjects/validation/useActiveProjectValidation";
import { roundNumericString } from "@/utils/helper";

export type NutritionStatus = "active" | "inactive" | "draft";

export const PDCAAS_MIN = 0;
export const PDCAAS_MAX = 1;

/**
 * Restrict PDCAAS while typing: numeric only, max 2 decimals, range [0, 1].
 * Returns `null` when the change should be rejected.
 */
export function sanitizePdcaasOnChange(raw: string): string | null {
  const inputValue = String(raw ?? "");
  if (inputValue === "") return "";

  const regex = /^(\d*\.?\d{0,2})$/;
  if (!regex.test(inputValue)) return null;

  if (inputValue === "." || inputValue.endsWith(".")) {
    const prefix = inputValue === "." ? "" : inputValue.slice(0, -1);
    if (prefix === "" || prefix === "0" || prefix === "1" || prefix === "0." || prefix === "1.") {
      return inputValue;
    }
    const n = parseFloat(prefix || "0");
    if (!Number.isFinite(n) || n > PDCAAS_MAX) return null;
    return inputValue;
  }

  const n = parseFloat(inputValue);
  if (!Number.isFinite(n)) return inputValue;
  if (n < PDCAAS_MIN) return "0";
  if (n > PDCAAS_MAX) return "1";

  return inputValue;
}

/** True when the user's raw input is a complete number outside [0, 1] (field may already be clamped). */
export function isPdcaasInputAttemptOutOfRange(raw?: string | null): boolean {
  const inputValue = String(raw ?? "").trim();
  if (!inputValue || inputValue === "." || inputValue.endsWith(".")) return false;

  const regex = /^(\d*\.?\d{0,2})$/;
  if (!regex.test(inputValue)) return false;

  const n = parseFloat(inputValue);
  if (!Number.isFinite(n)) return false;
  return n < PDCAAS_MIN || n > PDCAAS_MAX;
}

/** Normalize on blur / load / save: clamp to [0, 1] and round to 2 decimals. */
export function normalizePdcaasBlur(raw?: string | null): string {
  const rounded = roundNumericString(raw);
  if (!rounded) return "";

  const n = parseFloat(rounded);
  if (!Number.isFinite(n)) return "";

  const clamped = Math.min(PDCAAS_MAX, Math.max(PDCAAS_MIN, n));
  return roundNumericString(clamped);
}

export function parsePdcaasForPayload(value?: string): number | undefined {
  const normalized = normalizePdcaasBlur(value);
  if (!normalized) return undefined;

  const num = parseFloat(normalized);
  return Number.isFinite(num) ? num : undefined;
}

export function isPdcaasInValidRange(value?: string | null): boolean {
  const normalized = normalizePdcaasBlur(value);
  if (!normalized) return false;

  const num = parseFloat(normalized);
  return Number.isFinite(num) && num >= PDCAAS_MIN && num <= PDCAAS_MAX;
}

const useNutrition = (
  status: NutritionStatus = "active"
) => {
  const { nutritionForm, setNutritionForm, setNutritionSections } = useFormContext();
  const { selectedActiveProject } = useGlobalStateContext();
  const { updateSectionValidationFlag } = useActiveProjectValidation();
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Manage transitions in hook
  const [availableTransitions, setAvailableTransitions] = useState<Array<{ label: string; transitionId: string }>>([]);

  /**
   * Load nutrition data from the new getNutritionSections API
   * This replaces the JSON store pattern with direct API calls
   * @param productSpecificationId - The product specification ID (kept for backward compatibility, not used by API)
   * @param assessmentId - Required assessment ID
   */
  const loadNutritionFromApi = useCallback(async (
    productSpecificationId: string | number,
    assessmentId: string,
    options?: { skipLoading?: boolean }
  ) => {
    if (!options?.skipLoading) {
      setIsLoading(true);
    }
    setError(null);

    try {
      if (!assessmentId) {
        throw new Error('Assessment ID is required for fetching nutrition data');
      }
      
      // Fetch from new getNutritionSections API (sections/nutrition endpoint)
      const response = await NutrientService.getNutritionSections(assessmentId);

      // Check if response is empty or null
      if (!response?.nutritionTab?.length) {
        
        // Initialize form with empty/default values
        setNutritionForm((prev: any) => ({
          ...prev,
          ...NutritionFormInitial
        }));
        
        setData([]);
        return null;
      }

      // Find section by productSpecificationId, fallback to first
      const selectedSection = response.nutritionTab?.find(
        (tab: any) => String(tab.id) === String(productSpecificationId)
      ) || response.nutritionTab?.[0];

      // Extract transitions from response and update hook state
      // Note: GetNutritionSectionsResponse doesn't include transitionObject, transitions are handled separately
      // Only update if transitions are present in response, otherwise preserve existing
      const transitionsFromResponse = (response as any).transitionObject && (response as any).transitionObject.data && Array.isArray((response as any).transitionObject.data) && (response as any).transitionObject.data.length > 0
        ? (response as any).transitionObject.data
        : null;
      
      if (transitionsFromResponse) {
        setAvailableTransitions(transitionsFromResponse);
      }

      // Transform the selected section to frontend format
      // Cast to any since transformer handles multiple formats including direct objects
      const transformedData = transformGetNutritionResponse(selectedSection as any);

      // Update form with transformed data (includes packCopy if present)
      setNutritionForm((prev: any) => ({
        ...prev,
        ...transformedData,
        // Explicitly set packCopy from API response if available
        packCopy: (response as any)?.packCopy || undefined
      }));

      // Store the raw response for reference (wrap in array for consistency with existing pattern)
      setData([response]);

      return response;

    } catch (error: any) {
      console.error(" [useNutrition] Error fetching nutrition data from API:", error);
      
      // Check if it's a 404 (not found) or other error
      const is404 = error?.response?.status === 404 || error?.status === 404;
      const errorMessage = is404 
        ? "No nutrition data found - starting with empty form" 
        : error.message || "Failed to load nutrition data";
      
      setError(errorMessage);
      setData([]);
      
      // For 404 or any error, initialize form with empty values
      // This ensures the form is in a clean, usable state
      // console.log([useNutrition] ${errorMessage}, initializing form with default values`);
      
      setNutritionForm((prev: any) => ({
        ...prev,
        ...NutritionFormInitial
      }));
      
      // Return null to indicate no data was loaded
      return null;
      
    } finally {
      if (!options?.skipLoading) {
        setIsLoading(false);
      }
    }
  }, [setNutritionForm]);

  /**
   * Load nutrition sections from the getNutritionSections API
   * This fetches all nutrition tabs/sections for a given assessment
   * @param assessmentId - Required assessment ID
   * @param options - Optional configuration options
   */
  const loadNutritionSectionsFromApi = useCallback(async (
    assessmentId: string | number,
    options?: { skipLoading?: boolean }
  ) => {
    if (!options?.skipLoading) {
      setIsLoading(true);
    }
    setError(null);

    try {
      console.log(' [useNutrition] Loading nutrition sections from API:', { assessmentId });
      
      if (!assessmentId) {
        throw new Error('Assessment ID is required for fetching nutrition sections');
      }
      
      // Fetch from getNutritionSections API
      const response = await NutrientService.getNutritionSections(assessmentId);
      
      console.log(' [useNutrition] Raw getNutritionSections API response:', response);

      // Check if response is empty or null
      // Note: Creation of nutrition tabs is handled by useNutritionSections hook
      // to avoid duplicate API calls
      if (!response || !response.nutritionTab || response.nutritionTab.length === 0) {
        console.warn('⚠️ [useNutrition] API returned empty nutrition sections');
        setNutritionSections(null);
        return null;
      }

      // Store the response in FormContext
      setNutritionSections(response);

      console.log(' [useNutrition] Nutrition sections loaded successfully:', {
        sectionCount: response.nutritionTab.length,
        sections: response.nutritionTab.map(section => ({
          id: section.id,
          sectionName: section.sectionName,
          isNutritionValidated: section.isNutritionValidated
        }))
      });

      return response;

    } catch (error: any) {
      console.error(" [useNutrition] Error fetching nutrition sections from API:", error);
      
      // Check if it's a 404 (not found) or other error
      const is404 = error?.response?.status === 404 || error?.status === 404;
      const errorMessage = is404 
        ? "No nutrition sections found" 
        : error.message || "Failed to load nutrition sections";
      
      setError(errorMessage);
      
      // Set to null on error
      setNutritionSections(null);
      
      // Return null to indicate no data was loaded
      return null;
      
    } finally {
      if (!options?.skipLoading) {
        setIsLoading(false);
      }
    }
  }, [setNutritionSections]);

  /**
   * Save nutrition data using the new saveNutrition API
   * Replaces the JSON store save pattern
   * @param productSpecificationId - The product specification ID
   * @param includeTransitionObject - Whether to include transition object (for share for review)
   * @param transitionObject - Optional transition object
   * @param recipeName - Optional recipe name (variant label)
   * @param isNutritionValidated - Whether nutrition data passed validation
   * @param revisionNotes - Optional revision notes for request for revision
   * @param vendorAssessmentId - Optional vendor assessment ID to include in URL parameter
   * @param silentFlush - When true (vendor version flush), skip hook loading state so global UI is unchanged
   * @returns Promise with API response
   */
  const saveNutritionToApi = useCallback(async (
    productSpecificationId: number,
    includeTransitionObject: boolean = false,
    transitionObject?: { transitionId: string; objectId: string },
    recipeName?: string,
    isNutritionValidated?: boolean,
    revisionNote?: { notes: string; id?: string | number },
    vendorAssessmentId?: string | number,
    silentFlush?: boolean
  ) => {
    if (!silentFlush) {
      setIsLoading(true);
    }
    setError(null);

    try {
      // Import transformer function
      const { transformNutritionFormToSavePayload } = await import('@/utils/saveNutritionTransformer');
      
      // Transform form data to API payload
      // Detect Pet Food from active project context (preferred)
      const isPetFood = ((selectedActiveProject?.productSpecificationType || selectedActiveProject?.category || '') as string)
        .toLowerCase()
        .includes('pet');

      const payload = transformNutritionFormToSavePayload(
        nutritionForm,
        productSpecificationId,
        includeTransitionObject,
        transitionObject,
        recipeName,
        isNutritionValidated,
        isPetFood,
        revisionNote,
        undefined,
        undefined
      );
      
      const payloadLog: any = {
        nutritionTabId: payload.nutritionTabId,
        recipeName: payload.recipeName,
        isNutritionValidated: payload.isNutritionValidated,
        hasTransitionObject: !!payload.transitionObject,
        nutrientCount: payload.nutrientList?.length || 0,
        attachmentCount: payload.attachments?.length || 0,
        servingQuantity: {
          original: nutritionForm.servingQuantityForCalculation,
          extracted: payload.servingQuantityForCalculations,
          unit: payload.servingQuantityForCalculationsUom
        },
        column2Unit: payload.column2Unit
      };
      // Only show revisionNote if it exists in the final payload
      if (payload.revisionNote) {
        payloadLog.revisionNote = payload.revisionNote;
      }
      
      // Call API
      const response = await NutrientService.saveNutrition(payload, vendorAssessmentId);
      
      // Update form with returned ID and validation status (store as boolean for UI)
      // Note: genesisId is already in the form state and is preserved
      setNutritionForm((prev: any) => ({
        ...prev,
        id: response.id,
        isNutritionValidated: payload.isNutritionValidated === "true"
      }));

      if(payload.isNutritionValidated === "true") {
        updateSectionValidationFlag('nutrition', String(productSpecificationId));
      }
      
      return response;
      
    } catch (error: any) {
      console.error(" [useNutrition] Error saving nutrition data to API:", error);
      setError(error.message || "Failed to save nutrition data");
      throw error;
    } finally {
      if (!silentFlush) {
        setIsLoading(false);
      }
    }
  }, [nutritionForm, selectedActiveProject?.productSpecificationType, selectedActiveProject?.category, selectedActiveProject?.title, updateSectionValidationFlag]);

  /**
   * Load nutrient listing data from API
   * Should be called during page load to populate nutrients
   * @param productSpecificationId - The product specification ID
   */
  const loadNutrientListingData = useCallback(async (
    productSpecificationId: string | number,
    mandatoryNutrients: any[] = []
  ) => {
    try {
      console.log(' [useNutrition] Loading nutrient listing data for:', productSpecificationId);
      console.log(' [useNutrition] Mandatory nutrients to preserve:', mandatoryNutrients.length);
      
      // Import transformer functions
      const { transformNutrientListingResponse, mergeNutrientsByName } = await import('@/utils/nutrientListingTransformer');
      
      // Fetch nutrient listing
      const response = await NutrientService.getNutrientListData(productSpecificationId);
      
      console.log(' [useNutrition] Nutrient listing response:', response);
      
      if (response && response.objects) {
        // Transform API response
        const transformedNutrients = transformNutrientListingResponse(response);
        console.log(' [useNutrition] Transformed nutrients from API:', transformedNutrients);
        
        // Merge with existing nutrients AND mandatory nutrients
        setNutritionForm((prev: any) => {
          const existingNutrients = prev.nutrients || [];
          
          // First, ensure mandatory nutrients are in the base list
          const baseNutrients = [...existingNutrients];
          mandatoryNutrients.forEach((mandatory: any) => {
            const normalizedMandatoryName = mandatory.nutrient?.replace(' *', '').trim().toLowerCase();
            const exists = baseNutrients.find(n => 
              n.nutrient?.replace(' *', '').trim().toLowerCase() === normalizedMandatoryName
            );
            if (!exists) {
              baseNutrients.push(mandatory);
            }
          });
          
          // Then merge with API data
          const mergedNutrients = mergeNutrientsByName(baseNutrients, transformedNutrients);
          
          return {
            ...prev,
            nutrients: mergedNutrients
          };
        });
      } else {
        // If no API response, ensure mandatory nutrients are at least in the form
        setNutritionForm((prev: any) => {
          const existingNutrients = prev.nutrients || [];
          const baseNutrients = [...existingNutrients];
          
          mandatoryNutrients.forEach((mandatory: any) => {
            const normalizedMandatoryName = mandatory.nutrient?.replace(' *', '').trim().toLowerCase();
            const exists = baseNutrients.find(n => 
              n.nutrient?.replace(' *', '').trim().toLowerCase() === normalizedMandatoryName
            );
            if (!exists) {
              baseNutrients.push(mandatory);
            }
          });
          
          return {
            ...prev,
            nutrients: baseNutrients
          };
        });
      }
      
      return response;
    } catch (error: any) {
      console.error(" [useNutrition] Error fetching nutrient listing:", error);
      // Don't throw - allow the form to continue with mandatory nutrients only
      return null;
    }
  }, [setNutritionForm]);

  // Filter data based on status if needed in the future
  const filteredData = data;

  return {
    loadNutritionFromApi, // Load nutrition data from API
    loadNutritionSectionsFromApi, // Load nutrition sections from API
    loadNutrientListingData, // Load nutrient listing from API
    saveNutritionToApi, // Save nutrition data via API
    normalizePdcaasBlur,
    sanitizePdcaasOnChange,
    parsePdcaasForPayload,
    isPdcaasInValidRange,
    isPdcaasInputAttemptOutOfRange,
    availableTransitions, // Available workflow transitions (managed by hook or component via callback)
    data: filteredData,
    loading: isLoading,
    error,
    reload: async () => {
      setData([]);
      setError(null);
    }
  };
};

export default useNutrition;
